/**
 * Mandatory terms acceptance in the cart.
 *
 * Regulamin § 4 and § 11 make every order a separate contract that needs its own acceptance,
 * and Shopify checkout is out of reach on this plan (checkout UI extensions are Plus-only),
 * so the cart is where the opt-in lives.
 *
 * Three things happen here:
 * 1. the checkout submit is blocked until the box is ticked,
 * 2. accelerated checkout buttons (Shop Pay, PayPal) are blocked too — they bypass the form,
 * 3. the acceptance is written into a cart attribute with a timestamp, so it reaches the order
 *    and survives the section re-render that follows any quantity change.
 */
if (!customElements.get('cart-terms')) {
  customElements.define(
    'cart-terms',
    class CartTerms extends HTMLElement {
      connectedCallback() {
        this.checkbox = this.querySelector('.cart-terms__checkbox');
        this.errorMessage = this.querySelector('.cart-terms__error');
        this.attributeInput = this.querySelector('[name="attributes[Akceptacja regulaminu]"]');
        this.form = document.getElementById(this.dataset.formId);

        if (!this.checkbox || !this.form) return;

        this.checkbox.addEventListener('change', () => this.onChange());

        this.form.addEventListener('submit', (event) => {
          if (!this.guard()) event.preventDefault();
        });

        /*
          Accelerated checkout buttons render outside the cart form and go straight to checkout,
          so the submit handler above never sees them. Capture phase, because the button lives
          inside an iframe-backed widget that stops propagation of its own clicks.
        */
        const accelerated = document.querySelector('.cart__dynamic-checkout-buttons');
        if (accelerated) {
          accelerated.addEventListener(
            'click',
            (event) => {
              if (this.guard()) return;
              event.preventDefault();
              event.stopPropagation();
            },
            true
          );
        }
      }

      /** Returns true when checkout may proceed; otherwise surfaces the error and refuses. */
      guard() {
        if (this.checkbox.checked) {
          this.clearError();
          return true;
        }

        this.errorMessage.hidden = false;
        this.classList.add('cart-terms--invalid');
        this.checkbox.setAttribute('aria-invalid', 'true');
        this.checkbox.focus();
        this.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return false;
      }

      onChange() {
        this.attributeInput.value = this.checkbox.checked
          ? `${this.dataset.valuePrefix} (${CartTerms.timestamp()})`
          : '';

        if (this.checkbox.checked) this.clearError();
        this.save();
      }

      clearError() {
        this.errorMessage.hidden = true;
        this.classList.remove('cart-terms--invalid');
        this.checkbox.setAttribute('aria-invalid', 'false');
      }

      save() {
        fetch(window.routes?.cart_update_url || '/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            attributes: { 'Akceptacja regulaminu': this.attributeInput.value },
          }),
        }).catch(() => {
          // Silent: the value is also part of the cart form, so it still reaches checkout.
        });
      }

      /** Warsaw time, so the record matches the seller's own clock rather than the buyer's. */
      static timestamp() {
        return new Intl.DateTimeFormat('pl-PL', {
          timeZone: 'Europe/Warsaw',
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        }).format(new Date());
      }
    }
  );
}
